import axios from "axios";
import {
  deleteAPI,
  getAPI,
  patchAPI,
  persistXsrfTokenFromResponse,
  postAPI,
  putAPI,
  setXsrfToken,
} from "../utils/api";
import endpoints from "../utils/endpoints";

const normalizeMobile = (mobile) =>
  typeof mobile === "string" ? mobile.trim().replace(/\s+/g, "") : mobile;

const normalizeLoginPayload = (payload = {}) => ({
  ...payload,
  mobile: normalizeMobile(payload.mobile),
});

export const normalizeVerifyOtpResponse = (response) => {
  const httpStatus = response?.status;
  const body = response?.data ?? response;
  const payload =
    typeof body?.data === "object" &&
    body?.data !== null &&
    !Array.isArray(body.data)
      ? body.data
      : body;

  const nestedAuth =
    typeof payload?.authResponse === "object" ? payload.authResponse : null;

  const csrfToken =
    persistXsrfTokenFromResponse(response) ||
    payload?.csrfToken ||
    nestedAuth?.csrfToken ||
    body?.csrfToken ||
    null;

  if (csrfToken) {
    setXsrfToken(csrfToken);
  }

  const token =
    nestedAuth?.token ??
    nestedAuth?.accessToken ??
    payload?.token ??
    payload?.accessToken ??
    null;

  const role = String(nestedAuth?.role ?? payload?.role ?? "").toUpperCase();

  const authResponse = {
    ...(nestedAuth || {}),
    token,
    role,
    id:
      nestedAuth?.id ??
      nestedAuth?.userId ??
      payload?.id ??
      payload?.userId ??
      null,
  };

  const userExists =
    payload?.userExists === true ||
    payload?.userExists === "true" ||
    (payload?.userExists !== false && Boolean(token));

  const isAllowedRole = role === "ADMIN" || role === "SUPERADMIN";
  const isHttpSuccess = httpStatus === 200 || httpStatus === 201;

  return {
    httpStatus,
    userExists,
    isAllowedRole,
    authResponse,
    csrfToken,
    message: payload?.message ?? body?.message ?? null,
    isSuccess:
      isHttpSuccess && Boolean(token) && isAllowedRole && userExists,
  };
};

const getAuthErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const normalizeUsersResponse = (response) => {
  const body = response?.data ?? response;
  const payload =
    typeof body?.data === "object" && !Array.isArray(body?.data)
      ? body.data
      : body;

  const users =
    (Array.isArray(body) && body) ||
    (Array.isArray(body?.data) && body.data) ||
    payload?.users ||
    payload?.content ||
    body?.content ||
    [];

  const normalizedUsers = (Array.isArray(users) ? users : []).map((user) => ({
    ...user,
    walletBalance:
      user?.walletBalance ??
      user?.wallet?.balance ??
      user?.wallet?.walletBalance,
    walletCurrency: user?.walletCurrency ?? user?.wallet?.currency ?? 'INR',
  }));

  return {
    ...response,
    data: {
      ...(typeof body === "object" && !Array.isArray(body) ? body : {}),
      data: {
        ...(typeof payload === "object" && !Array.isArray(payload)
          ? payload
          : {}),
        users: normalizedUsers,
      },
    },
  };
};

export const login = (payload, callBack, errorCallback) => {
  return (dispatch) => {
    postAPI(
      endpoints?.SIGNIN,
      normalizeLoginPayload(payload),
      (response) => {
        persistXsrfTokenFromResponse(response);
        callBack(response);
      },
      (error) => {
        errorCallback({
          ...error,
          friendlyMessage: getAuthErrorMessage(
            error,
            "You don't have access to this portal"
          ),
        });
      }
    );
  };
};

export const verifyOtp = (payload, callBack, errorCallback) => {
  return (dispatch) => {
    postAPI(
      endpoints?.VERIFY_OTP,
      normalizeLoginPayload(payload),
      (response) => {
        callBack(normalizeVerifyOtpResponse(response));
      },
      (error) => {
        errorCallback({
          ...error,
          friendlyMessage: getAuthErrorMessage(
            error,
            "You don't have access to this portal"
          ),
        });
      }
    );
  };
};

export const getAllUsers = (callBack, errorCallback) => {
  return (dispatch) => {
    getAPI(
      endpoints?.GET_ALL_USERS,
      {},
      (response) => {
        callBack(normalizeUsersResponse(response));
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem("token")
    );
  };
};
export const getAllAdminUsers = (callBack, errorCallback) => {
  return (dispatch) => {
    getAPI(
      endpoints?.GET_ALL_USERS,
      {},
      (response) => {
        callBack(response);
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem("token")
    );
  };
};
export const getAllCampaignLeads = (callBack, errorCallback) => {
  return (dispatch) => {
    getAPI(
      endpoints?.GET_ALL_CAMPAIGN_LEADS,
      {},
      (response) => {
        callBack(response);
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem("token")
    );
  };
};
export const getAllBudgetreeLeads = (callBack, errorCallback) => {
  return (dispatch) => {
    getAPI(
      endpoints?.GET_ALL_BUDGETREE_LEADS,
      {},
      (response) => {
        callBack(response);
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem("token")
    );
  };
};

export const updateUserStatusById = (id, payload, callBack, errorCallback) => {
  return (dispatch) => {
    patchAPI(
      `${endpoints?.GET_ALL_USERS}/${id}/active`,
      payload,
      (response) => {
        callBack(response);
      },
      (error) => {
        errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};

export const userLogout = (payload, callBack, errorCallback) => {
  return (dispatch) => {
    postAPI(
      endpoints?.LOGOUT,
      {},
      (response) => {
        callBack(response);
      },
      (error) => {
        errorCallback(error);
      },
      payload.token
    );
  };
};
export const getUserByToken = (callBack, errorCallback, manualToken) => {
  return (dispatch) => {
    getAPI(
      endpoints?.GET_USER_BY_TOKEN,
      {},
      (response) => {
        callBack(response);
      },
      (error) => {
        errorCallback(error);
      },
      manualToken
    );
  };
};
export const getAllUsersClient = (payload, callBack, errorCallback) => {
  return (dispatch) => {
    getAPI(
      payload.page && payload.perPage
        ? `${endpoints?.GET_ALL_USERS}?page=${payload.page}&perPage=${payload.perPage}`
        : endpoints?.GET_ALL_USERS,

      {},
      (response) => {
        callBack(normalizeUsersResponse(response));
      },
      (err) => {
        errorCallback(err);
      },
      localStorage.getItem("token")
    );
  };
};
export const applyPermissions = (payload, callBack, errorCallback) => {
  return (dispatch) => {
    patchAPI(
      endpoints?.APPLY_PERMISSIONS,
      payload,
      (response) => {
        callBack(response);
      },
      (error) => {
        errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};
export const addBrands = (id, payload, callBack, errorCallback) => {
  return (dispatch) => {
    patchAPI(
      endpoints?.ADD_BRANDS + "/" + id,
      payload,
      (response) => {
        callBack(response);
      },
      (error) => {
        errorCallback(error);
      },
      localStorage.getItem("token")
    );
  };
};
export const imageUpload = (payload, callBack, errorCallback) => {
  return (dispatch) => {
    postAPI(
      endpoints?.IMAGE_UPLOAD,
      payload,
      (response) => {
        callBack(response);
      },
      (error) => {
        errorCallback(error);
      }
    );
  };
};

export const getFraudUsers = (payload, callback, errorCallback) => {
  return (dispatch) => {
    const queryParams = {
      page: payload?.page ?? 0,
      size: payload?.size ?? 20,
    };
    if (payload?.search !== undefined && payload?.search !== null && String(payload.search).trim() !== '') {
      queryParams.search = String(payload.search).trim();
    }

    const token = localStorage.getItem('token');

    getAPI(
      endpoints.GET_FRAUD_USERS,
      queryParams,
      (data) => {
        if (typeof callback === 'function') callback(data);
      },
      (err) => {
        if (err?.response?.status === 404) {
          getAPI(
            endpoints.GET_USERS_FRAUD,
            queryParams,
            (fallbackData) => {
              if (typeof callback === 'function') callback(fallbackData);
            },
            (fallbackErr) => {
              if (typeof errorCallback === 'function') errorCallback(fallbackErr);
            },
            token
          );
        } else if (typeof errorCallback === 'function') {
          errorCallback(err);
        }
      },
      token
    );
  };
};

export const updateUserFraudStatus = (userId, body, callback, errorCallback) => {
  return (dispatch) => {
    const endpoint = endpoints.UPDATE_FRAUD_USER.replace(':id', userId);
    const fallbackEndpoint = endpoints.UPDATE_USER_FRAUD.replace(':id', userId);
    const token = localStorage.getItem('token');

    patchAPI(
      endpoint,
      body,
      (res) => {
        if (typeof callback === 'function') callback(res);
      },
      (err) => {
        if (err?.response?.status === 404 || err?.response?.status === 405) {
          patchAPI(
            fallbackEndpoint,
            body,
            (res2) => {
              if (typeof callback === 'function') callback(res2);
            },
            (err2) => {
              // Try POST if PATCH is not allowed
              postAPI(
                endpoint,
                body,
                (res3) => {
                  if (typeof callback === 'function') callback(res3);
                },
                (err3) => {
                  if (typeof errorCallback === 'function') errorCallback(err3 || err2 || err);
                },
                token
              );
            },
            token
          );
        } else if (typeof errorCallback === 'function') {
          errorCallback(err);
        }
      },
      token
    );
  };
};

export const checkUserFraudStatus = (userId, callback, errorCallback) => {
  return (dispatch) => {
    const endpoint = endpoints.CHECK_USER_FRAUD.replace(':id', userId);
    getAPI(
      endpoint,
      {},
      (res) => {
        if (typeof callback === 'function') callback(res);
      },
      (err) => {
        if (typeof errorCallback === 'function') errorCallback(err);
      },
      localStorage.getItem('token')
    );
  };
};

